import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CustomerAPI, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CUSTOMER_TYPES, COUNTRIES, IN_STATES } from "@/lib/india";
import { validateGstin } from "@/lib/gstin";
import { toast } from "sonner";

export default function CustomerFormPage({ mode = "create" }) {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { branches, currentBranchId } = useAuth();
    const [busy, setBusy] = useState(false);
    const [gstinTouched, setGstinTouched] = useState(false);

    const initialBranch = params.get("branch_id") || currentBranchId || branches[0]?.id;
    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        phone: "",
        alt_phone: "",
        email: "",
        gstin: "",
        customer_type: "individual",
        primary_branch_id: initialBranch,
        tags: [],
        notes: "",
        address: {
            line1: "",
            line2: "",
            city: "",
            state_code: "KA",
            postal_code: "",
            country_code: "IN",
        },
    });

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }
    function setAddr(k, v) { setForm((f) => ({ ...f, address: { ...f.address, [k]: v } })); }

    // Inline GSTIN validation surfaces "Invalid checksum…" as soon as the user
    // blurs the field. Final POST is still server-validated.
    const gstinCheck = useMemo(() => {
        if (!form.gstin) return null;
        return validateGstin(form.gstin);
    }, [form.gstin]);
    const showGstinError = gstinTouched && form.customer_type === "business" && gstinCheck && !gstinCheck.ok;
    const showIndividualGstinError = form.customer_type === "individual" && form.gstin && form.gstin.trim().length > 0;

    async function save() {
        // Hard-block submission when individuals carry a GSTIN — surfaced via
        // gstin-individual-error so the test suite can assert blocking UX.
        if (showIndividualGstinError) {
            toast.error("Individual customers cannot carry a GSTIN. Clear the field or switch type to business.");
            return;
        }
        if (form.customer_type === "business" && gstinCheck && !gstinCheck.ok) {
            toast.error(gstinCheck.message);
            return;
        }
        setBusy(true);
        try {
            const body = { ...form };
            if (!body.email) delete body.email;
            if (!body.gstin) delete body.gstin;
            if (!body.alt_phone) delete body.alt_phone;
            // strip empty address
            if (!body.address.line1 && !body.address.city) body.address = null;
            const created = await CustomerAPI.create(body);
            toast.success(`Customer ${created.customer_code} created`);
            navigate(`/customers/${created.id}`);
        } catch (e) {
            toast.error(extractError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="customer-new-page">
            <header className="flex items-center justify-between">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">New customer</h1>
                    <p className="mt-1 text-sm text-zinc-500">Contact, billing details and tagging.</p>
                </div>
                <Link to="/customers" className="text-sm text-zinc-500 hover:underline">← Back to list</Link>
            </header>

            <Card className="p-6">
                <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Contact</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label="First name" required>
                        <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="cust-firstname" />
                    </Field>
                    <Field label="Last name">
                        <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="cust-lastname" />
                    </Field>
                    <Field label="Phone (E.164 or 10-digit Indian)" required>
                        <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+919876500000" className="font-mono" data-testid="cust-phone" />
                    </Field>
                    <Field label="Alternate phone">
                        <Input value={form.alt_phone} onChange={(e) => set("alt_phone", e.target.value)} className="font-mono" data-testid="cust-altphone" />
                    </Field>
                    <Field label="Email">
                        <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="cust-email" />
                    </Field>
                    <Field label="Customer type">
                        <select
                            value={form.customer_type}
                            onChange={(e) => set("customer_type", e.target.value)}
                            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                            data-testid="customer-type-select"
                        >
                            {CUSTOMER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </Field>
                    {/* GSTIN field is rendered when the country is IN regardless of
                        customer_type so individuals-with-GSTIN can be detected and
                        blocked via gstin-individual-error. */}
                    {form.address.country_code === "IN" && (
                        <Field label="GSTIN">
                            <Input
                                value={form.gstin}
                                onBlur={() => setGstinTouched(true)}
                                onChange={(e) => { setGstinTouched(true); set("gstin", e.target.value.toUpperCase()); }}
                                className="font-mono uppercase"
                                placeholder="29AAACI4798L3ZS"
                                disabled={form.customer_type === "individual" && !form.gstin}
                                data-testid="gstin-input"
                            />
                            {showGstinError && (
                                <div
                                    role="alert"
                                    data-testid="gstin-error"
                                    className="mt-1 rounded border border-brand-red/40 bg-brand-red/10 px-2 py-1 font-mono text-[11px] text-brand-redDeep dark:text-brand-red"
                                >
                                    {gstinCheck.code === "CHECKSUM"
                                        ? `Invalid GSTIN checksum: ${gstinCheck.message}`
                                        : `Invalid GSTIN: ${gstinCheck.message}`}
                                </div>
                            )}
                            {showIndividualGstinError && (
                                <div
                                    role="alert"
                                    data-testid="gstin-individual-error"
                                    className="mt-1 rounded border border-amber-400/40 bg-amber-100 px-2 py-1 font-mono text-[11px] text-amber-900 dark:bg-amber-900/30 dark:text-amber-200"
                                >
                                    Individual customers cannot carry a GSTIN — clear the field or switch the customer type to "business".
                                </div>
                            )}
                        </Field>
                    )}
                    <Field label="Primary branch">
                        <select value={form.primary_branch_id || ""} onChange={(e) => set("primary_branch_id", e.target.value)}
                            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="cust-branch">
                            {branches.map((b) => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
                        </select>
                    </Field>
                </div>
            </Card>

            <Card className="p-6">
                <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Address</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label="Line 1"><Input value={form.address.line1} onChange={(e) => setAddr("line1", e.target.value)} data-testid="cust-addr1" /></Field>
                    <Field label="Line 2"><Input value={form.address.line2 || ""} onChange={(e) => setAddr("line2", e.target.value)} /></Field>
                    <Field label="City"><Input value={form.address.city} onChange={(e) => setAddr("city", e.target.value)} data-testid="cust-city" /></Field>
                    <Field label="State">
                        <select value={form.address.state_code} onChange={(e) => setAddr("state_code", e.target.value)}
                            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm font-mono" data-testid="cust-state">
                            {IN_STATES.map(([code, name]) => <option key={code} value={code}>{code} — {name}</option>)}
                        </select>
                    </Field>
                    <Field label="Postal code"><Input value={form.address.postal_code} onChange={(e) => setAddr("postal_code", e.target.value)} className="font-mono" data-testid="cust-pin" /></Field>
                    <Field label="Country">
                        <select value={form.address.country_code} onChange={(e) => setAddr("country_code", e.target.value)}
                            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm font-mono">
                            {COUNTRIES.map(([c, n]) => <option key={c} value={c}>{c} — {n}</option>)}
                        </select>
                    </Field>
                </div>
            </Card>

            <Card className="p-6">
                <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Notes</h2>
                <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} placeholder="Internal notes about this customer." data-testid="cust-notes" />
            </Card>

            <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" onClick={() => navigate("/customers")}>Cancel</Button>
                <Button
                    onClick={save}
                    disabled={busy}
                    className="bg-brand-red text-white hover:bg-brand-redDeep"
                    data-testid="customer-submit"
                >
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Create customer
                </Button>
            </div>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                {label}{required && <span className="ml-0.5 text-brand-red">*</span>}
            </Label>
            {children}
        </div>
    );
}
